import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {
    updateGuestForm,
    setActiveStepAction,
    nextStepAction,
    prevStepAction,
    saveGuestTravel,
} from "~/redux/actions/guests";

import classnames from "classnames";
import produce from "immer";
import Flight from "./Flight";
import Train from "./Train";
import Car from "./Car";
import { PlaneIcon, TrainIcon, CarIcon } from '../../../Components/Icons/Icons';
import StepsNavigation from '../StepsNavigation';
import TextTranslate, { TextTranslateString } from '../../../Components/TextTranslate/TextTranslate';
import ToggleSwitch from "~/Components/ToggleSwitch/ToggleSwitch";
import { updateGuestFormUtil } from '../Utils/utils';

const Travel = ({
    formData,
    activeGuest,
    activeStep,
    guests,
    event,
    isAdmin = false,
    ...props}) =>
{

    const [error, setError] = useState(null);

    const guest = !isAdmin ? guests[activeGuest] : guests;
    const step = !isAdmin ? activeStep[activeGuest] : undefined;
    const eventName = event?.name || 'the event.';

    let form = !isAdmin ? formData[activeGuest][step] : formData;

    const defaultValues = {
        travel_method: 'flight',
        travel_circuit_only: false,
        travel_ground_transfers: false,
        travel_enabled: false,
        flight: {
            outbound: {},
            inbound: {},
        },
        train: {
            outbound: {},
            inbound: {},
        },
        car: {}
    };

    const tabs = {
        flight: TextTranslateString("travel-flight", "Flight", props.currentLanguage, props.translations),
        train: TextTranslateString("travel-train", "Train", props.currentLanguage, props.translations),
        car: TextTranslateString("travel-car", "Car", props.currentLanguage, props.translations),
    };

    form = {...defaultValues, ...form};

    const handleSwitch = (field) => {

        form = produce(form, (draft) => {
            draft[field] = !draft[field];
        });

        updateGuestFormUtil(form, isAdmin, props.onChange, props.dispatch);
    }

    const handleTabChange = (tab) => {

        const newForm = produce(form, (draft) => {
            draft.travel_method = tab;
        });

        updateGuestFormUtil(newForm, isAdmin, props.onChange, props.dispatch);
    }

    const handleFlightChange = (formData) => {

        const newForm = produce(form, (draft) => {
            draft.flight = formData;
        });

        updateGuestFormUtil(newForm, isAdmin, props.onChange, props.dispatch);
    }

    const handleTrainChange = (formData) => {

        const newForm = produce(form, (draft) => {
            draft.train = formData;
        });

        updateGuestFormUtil(newForm, isAdmin, props.onChange, props.dispatch);
    }

    const handleCarChange = (formData) => {

        const newForm = produce(form, (draft) => {
            draft.car = formData;
        });

        updateGuestFormUtil(newForm, isAdmin, props.onChange, props.dispatch);
    }

    const handlePrevious = () => {
        props.dispatch(prevStepAction())
    }

    const handleNext = () => {
        let promise = props.dispatch(saveGuestTravel(guest.id, form));

        promise.then((data) => {
            setError(null);

            props.dispatch(nextStepAction());

        }).catch(error => setError(error.data));
    }

    const methodIconRender = (tab) => {
        switch(tab) {
            case 'flight': return <PlaneIcon />
            case 'train': return <TrainIcon />
            case 'car': return <CarIcon />
            default: return null
        }
    }

    useEffect(() => {
        setError(props?.errors);
    }, [props?.errors])

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <ToggleSwitch
                        label2={TextTranslateString("travel-switch-circuit-only", "Will you be joining the experience at the circuit only?", props.currentLanguage, props.translations)}
                        onChange={() => handleSwitch('travel_circuit_only')}
                        isChecked={form.travel_circuit_only}
                        id="circuitOnly"
                    />
                    <small className="d-block">
                        <TextTranslate name="travel-circuit-only-note" fallback='Please note: No event parking is available, only "Passenger drop off zones".' textOnly/>
                    </small>
                </div>
            </div>
            <hr />
            {guest?.invitation?.event?.ground_transfer ? (
                <>
                    <div className="row">
                        <div className="col-12">
                            <ToggleSwitch
                                label2={TextTranslateString("travel-switch-ground-transfers", "Will you require ground transfers?", props.currentLanguage, props.translations)}
                                onChange={() => handleSwitch('travel_ground_transfers')}
                                isChecked={form.travel_ground_transfers}
                                id="travelGroundTransfers"
                            />
                            <small className="d-block">
                                <TextTranslate name="travel-ground-transfers-only-note" fallback="Please note: If you selected 'Yes', a member of the NEOM Experience team will be in touch to make arrangements." textOnly/>
                            </small>
                        </div>
                    </div>
                    <hr />
                    {form.travel_ground_transfers && (
                        <>
                            <b><TextTranslate name="travel-ground-transfers-confirm" fallback="You have selected ‘yes’ to requiring ground transfers. A member of the NEOM Experience team will be in touch to make arrangements." textOnly/></b>
                            <hr />
                        </>
                    )}
                </>
            )
            : null}

            <div className="row">
                <div className="col-12">
                    <ToggleSwitch
                        label2={TextTranslateString("travel-switch-more-information", "Do you have further information to share?", props.currentLanguage, props.translations)}
                        onChange={() => handleSwitch('travel_enabled')}
                        isChecked={form.travel_enabled}
                        id="travelEnabled"
                    />
                    <small className="d-block">
                        <TextTranslate name="travel-more-information-note" fallback="Please select the 'car' option if you are meeting us at the circuit." textOnly/>
                    </small>
                </div>
            </div>
            <hr />
            {form.travel_enabled && (
                <>
                    <div className="row">
                        <div className="col-12">
                            {error?.message && (
                                <div className="alert alert-danger mt-3" role="alert">
                                    {error.message}
                                </div>
                            )}

                            <h3><TextTranslate name="travel-transport-method" fallback="Transport Method" textOnly/></h3>
                            <p><TextTranslate name="travel-more-information-note" fallback="Please select which transport method you are planning to use." textOnly/></p>

                            <ul className="travel-tabs list-group list-group-horizontal mt-4">
                                {Object.keys(tabs).map((tab) => {
                                    return (
                                        <li key={tab}
                                            className={classnames([
                                                "cursor-pointer",
                                                "list-group-item",
                                                {active: tab === form.travel_method},
                                            ])}
                                            onClick={() => handleTabChange(tab)}
                                        >
                                            <a>{tabs[tab]}</a>
                                            { methodIconRender(tab) }
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    {form.travel_method === 'flight' && (
                        <Flight
                            values={form.flight}
                            onChange={(form) => handleFlightChange(form)}
                            errors={error?.errors}
                        />
                    )}

                    {form.travel_method === 'train' && (
                        <Train values={form.train} onChange={handleTrainChange} errors={error?.errors}/>
                    )}
                    {form.travel_method === 'car' && (
                        <Car values={form.car} onChange={handleCarChange} errors={error?.errors}/>
                    )}
                </>
            )}

            <h2 className="text-headline mt-2 mb-2">VISA Requirements</h2>
            <p className="m-0 text-subhead font-weight-normal">Please ensure you have researched and actioned any visa requirements for the {eventName}</p>
            <p className="text-subhead font-weight-normal">For more information on visa requirements for the {eventName}, please click <a href={event?.visa_link} target="_blank" className="text-primary link">here.</a></p>

            {!isAdmin ?
                <StepsNavigation
                    fnPrevious={handlePrevious}
                    fnNext={handleNext}
                /> : null
            }
        </>
    )
}

const mapStateToProps = (state, componentProps) => ({
    currentLanguage: state.translations?.currentLanguage,
    translations: state.translations?.translations,
    guests: state.guests?.list || componentProps.guest,
    activeGuest: state.guests?.activeGuest,
    activeStep: state.guests?.activeStep,
    formData: state.guests?.formData || componentProps.formData,
    event: state.invitation?.data?.event,
});

export default connect(mapStateToProps)(Travel);
